import {
  AddLink,
  Close,
  Sync,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
  Stack,
  Switch,
  Tooltip,
  Alert,
  keyframes,
} from "@mui/material";
import { useEffect, useState } from "react";
import { TypeDataSource } from "../../../../componentes/administracao/types";
import { useNotification } from "../../../../componentes/notificationProvider";
import { AxiosError } from "axios";
import api from "../../../../services/api";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/info.svg";

type OutletContextType = {
  setIsEditingDataSource: (val: boolean) => void;
};

export default function FormularioDataSource() {
  const [nome, setNome] = useState<string>("");
  const [host, setHost] = useState<string>("");
  const [database, setDatabase] = useState<string>("");
  const [port, setPort] = useState<number | null>(5432);
  const [user, setUser] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordSSH, setShowPasswordSSH] = useState(false);
  const [datasourceType, setDatasourceType] = useState<TypeDataSource | null>(
    null,
  );
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [hostSSH, setHostSSH] = useState<string>("");
  const [userSSH, setUserSSH] = useState<string>("");
  const [portSSH, setPortSSH] = useState<number | null>(22);
  const [passwordSSH, setPasswordSSH] = useState<string>("");
  const [hasSshTunnel, setHasSshTunnel] = useState<boolean>(false);
  const [messageSync, setMessageSync] = useState<string | null>(null);
  const [isSync, setIsSync] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { setIsEditingDataSource } = useOutletContext<OutletContextType>();

  const theme = useTheme();
  const notify = useNotification();
  const navigate = useNavigate();
  const styles = {
    paper: {
      padding: 3,
      gap: 3,
      width: "100%",
      flexDirection: "column",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      minWidth: "320px",
      height: "100%",
    },
    container: {
      padding: 3,
      minWidth: "320px",
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "100%",
    },
    boxRowFields: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      gap: 1,
    },
    subtitle: {
      fontSize: "14px",
      fontWeight: 500,
      color: "text.primary",
    },
    textfield: {
      backgroundColor: theme.palette.background.textField,
      width: "100%",
      borderRadius: 1,
      maxHeight: "56px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "transparent",
      },
      "& .MuiFormHelperText-root": {
        margin: 0,
        padding: 0,
        minHeight: "20px",
      },
    },
  };
  useEffect(() => {
    if (id) {
      const fetchDataSource = async () => {
        try {
          setLoading(true);
          const response = await api.post("/datasource/find", {
            id: id,
          });

          setNome(response.data.name);
          setHost(response.data.host);
          setDatabase(response.data.databaseName);
          setPort(response.data.port);
          setUser(response.data.userName);
          setPassword(response.data.password);
          setDatasourceType(
            TypeDataSource[
              response.data.enumType as keyof typeof TypeDataSource
            ],
          );
          setIsDefault(response.data.default);
          if (response.data.sshTunnel) {
            setHostSSH(response.data.sshTunnel.host);
            setPortSSH(response.data.sshTunnel.port);
            setUserSSH(response.data.sshTunnel.sshUser);
            setPasswordSSH(response.data.sshTunnel.sshPassword);
            setHasSshTunnel(true);
          }
        } catch (error) {
          console.error("Erro ao buscar data source:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchDataSource();
    }
    return;
  }, [id]);
  const handleClickShowPassword = (type: "password" | "ssh") => {
    if (type == "password") {
      setShowPassword((show) => !show);
    } else {
      setShowPasswordSSH((show) => !show);
    }
  };
  const handleMouseDownPassword = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();
  };
  async function handleSubmitDataSource() {
    setIsSubmit(true);
    if (
      !nome ||
      !host ||
      !port ||
      !datasourceType ||
      !user ||
      !password ||
      (hasSshTunnel === true &&
        (!hostSSH || !portSSH || !userSSH || !passwordSSH))
    )
      return;
    const key = (
      Object.keys(TypeDataSource) as (keyof typeof TypeDataSource)[]
    ).find((k) => TypeDataSource[k] === datasourceType);
    const formattedData = {
      name: nome,
      type: key,
      host: host,
      port: port,
      username: user,
      password: password,
      databaseName: database,
      sshData: hasSshTunnel
        ? {
            host: hostSSH,
            port: portSSH,
            sshUser: userSSH,
            sshPassword: passwordSSH,
          }
        : null,
      defaultDS: isDefault,
    };
    try {
      setLoading(true);
      let response;
      if (id) {
        response = await api.put("/datasource/update", {
          ...formattedData,
          id: id,
        });
      } else {
        response = await api.post("/datasource/create", {
          ...formattedData,
        });
      }

      if (response.data) {
        handleBack();
        notify(null, "Dados salvos com sucesso!", "success");
      }
    } catch (error: any) {
      console.log(error);
      if (error instanceof AxiosError && error?.response?.data && error.status)
        notify(error?.status || 500, undefined, "error", error);
      else notify(null, "Erro ao criar novo usuário!", "error");
    } finally {
      setLoading(false);
      setIsEditingDataSource(false);
      navigate("/configuracoes/datasource");
    }
  }

  async function handleConnectDataSource() {
    if (
      !host ||
      !port ||
      !datasourceType ||
      !user ||
      !password ||
      (hasSshTunnel === true &&
        (!hostSSH || !portSSH || !userSSH || !passwordSSH))
    )
      return;
    const key = (
      Object.keys(TypeDataSource) as (keyof typeof TypeDataSource)[]
    ).find((k) => TypeDataSource[k] === datasourceType);
    const formattedData = {
      data: {
        enumType: key,
        host: host,
        port: port,
        userName: user,
        password: password,
        databaseName: database,
      },
      sshTunnel: hasSshTunnel
        ? {
            host: hostSSH,
            port: portSSH,
            sshUser: userSSH,
            sshPassword: passwordSSH,
          }
        : null,
    };
    try {
      setLoading(true);
      const response = await api.post("/datasource/testConnection", {
        ...formattedData,
      });

      if (response.data.success) {
        setIsSync(true);
        setMessageSync(response.data.message);
      } else {
        setIsSync(false);
        setMessageSync(response.data.message);
      }
    } catch (error: any) {
      console.log(error);
      setIsSync(false);
      setMessageSync("Erro ao testar conexão com o Data Source");
    } finally {
      setLoading(false);
    }
  }

  const handleBack = () => {
    setNome("");
    setHost("");
    setDatabase("");
    setPort(5432);
    setUser("");
    setPassword("");
    setShowPassword(false);
    setDatasourceType(null);
    setIsSubmit(false);
  };

  const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `;

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        {messageSync && (
          <Alert
            sx={{ width: "100%" }}
            severity={isSync ? "success" : "error"}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageSync(null);
                }}
              >
                <Close fontSize="inherit" />
              </IconButton>
            }
          >
            {messageSync}
          </Alert>
        )}
        <Grid container width={"100%"} columnSpacing={1.5} rowSpacing={2}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>
                Nome para visualização
              </Typography>
              <TextField
                id="outlined-adornment-email"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                sx={styles.textfield}
                placeholder="Digite o nome"
                error={isSubmit && !nome}
                helperText={isSubmit && !nome && "Campo nome é obrigatório!"}
                disabled={loading}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 9, sm: 4 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Tipo do banco</Typography>
              <Autocomplete
                id="multiple-limit-tags"
                value={datasourceType}
                onChange={(e, value: TypeDataSource | null) =>
                  setDatasourceType(value)
                }
                options={Object.values(TypeDataSource)}
                getOptionLabel={(option: TypeDataSource) => option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione o tipo do banco"
                    sx={styles.textfield}
                    error={isSubmit && !datasourceType}
                    helperText={
                      isSubmit &&
                      !datasourceType &&
                      "Campo Tipo de banco é obrigatório"
                    }
                  />
                )}
                disabled={loading}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 3, sm: 1 }}>
            <Box sx={styles.boxRowFields}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",

                  gap: 1,
                }}
              >
                <Typography sx={{ ...styles.subtitle, mb: 0.8 }}>
                  Default
                </Typography>
                <Tooltip title="Data sources defaults serão pré selecionados ao criar novos painéis de gráficos e filtros.">
                  <Box>
                    <InfoIcon
                      style={{
                        fill: theme.palette.text.secondary,
                        height: "auto",
                        width: "1rem",
                      }}
                    />
                  </Box>
                </Tooltip>
              </Box>
              <Switch
                checked={isDefault}
                onChange={() => setIsDefault(!isDefault)}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Host</Typography>
              <TextField
                id="outlined-adornment-email"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                sx={styles.textfield}
                placeholder="Digite o host"
                error={isSubmit && !host}
                helperText={isSubmit && !host && "Campo Host é obrigatório!"}
                disabled={loading}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Nome do banco</Typography>
              <TextField
                id="outlined-adornment-email"
                value={database}
                onChange={(e) => setDatabase(e.target.value)}
                sx={styles.textfield}
                placeholder="Digite o nome do banco"
                disabled={loading}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Porta</Typography>
              <TextField
                id="outlined-adornment-email"
                value={port}
                type="number"
                onChange={(e) => setPort(Number(e.target.value))}
                sx={styles.textfield}
                placeholder="Porta"
                error={isSubmit && !port}
                helperText={isSubmit && !port && "Campo Porta é obrigatório!"}
                disabled={loading}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Usuário</Typography>
              <TextField
                id="outlined-adornment-email"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                sx={styles.textfield}
                placeholder="Digite o usuário do banco"
                error={isSubmit && !user}
                helperText={isSubmit && !user && "Campo Usuário é obrigatório!"}
                disabled={loading}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Senha</Typography>
              <TextField
                id="input-senha"
                placeholder="Digite a senha"
                sx={styles.textfield}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                slotProps={{
                  input: {
                    autoComplete: "new-password",
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => handleClickShowPassword("password")}
                          onMouseDown={() => handleMouseDownPassword}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                error={isSubmit && !password}
                helperText={
                  isSubmit && !password && "O campo Senha é obrigatório"
                }
                disabled={loading}
              />
            </Box>
          </Grid>
        </Grid>
        {hasSshTunnel && (
          <>
            <Stack
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              {" "}
              <Divider />
              <Typography variant="h6" color={theme.palette.text.primary}>
                Configuração SSH
              </Typography>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                Configure o túnel SSH para criar a ponte de conexão necessária
                entre o sistema e o servidor de banco de dados privado.
              </Typography>
            </Stack>
            <Grid container width={"100%"} columnSpacing={1.5} rowSpacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box sx={styles.boxRowFields}>
                  <Typography sx={styles.subtitle}>SSH Host</Typography>
                  <TextField
                    id="outlined-adornment-email"
                    value={hostSSH}
                    onChange={(e) => setHostSSH(e.target.value)}
                    sx={styles.textfield}
                    placeholder="Digite o host"
                    error={isSubmit && !hostSSH}
                    helperText={
                      isSubmit && !hostSSH && "Campo Host SSH é obrigatório!"
                    }
                    disabled={loading}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Box sx={styles.boxRowFields}>
                  <Typography sx={styles.subtitle}>SSH Porta</Typography>
                  <TextField
                    id="outlined-adornment-email"
                    value={portSSH}
                    type="number"
                    onChange={(e) => setPortSSH(Number(e.target.value))}
                    sx={styles.textfield}
                    placeholder="Porta"
                    error={isSubmit && !portSSH}
                    helperText={
                      isSubmit && !portSSH && "Campo Porta SSH é obrigatório!"
                    }
                    disabled={loading}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Box sx={styles.boxRowFields}>
                  <Typography sx={styles.subtitle}>SSH Usuário</Typography>
                  <TextField
                    id="outlined-adornment-email"
                    value={userSSH}
                    onChange={(e) => setUserSSH(e.target.value)}
                    sx={styles.textfield}
                    placeholder="Digite o usuário do banco"
                    error={isSubmit && !userSSH}
                    helperText={
                      isSubmit && !userSSH && "Campo Usuário SSH é obrigatório!"
                    }
                    disabled={loading}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Box sx={styles.boxRowFields}>
                  <Typography sx={styles.subtitle}>SSH Senha</Typography>
                  <TextField
                    id="input-senha-ssh"
                    placeholder="Digite a senha"
                    sx={styles.textfield}
                    type={showPasswordSSH ? "text" : "password"}
                    value={passwordSSH}
                    onChange={(e) => setPasswordSSH(e.target.value)}
                    slotProps={{
                      input: {
                        autoComplete: "new-password-ssh",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={() => handleClickShowPassword("ssh")}
                              onMouseDown={() => handleMouseDownPassword}
                            >
                              {showPasswordSSH ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    error={isSubmit && !passwordSSH}
                    helperText={
                      isSubmit &&
                      !passwordSSH &&
                      "O campo Senha SSH é obrigatório"
                    }
                    disabled={loading}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            width: "100%",
          }}
        >
          <Button
            variant="text"
            sx={{
              color:
                hasSshTunnel === true
                  ? theme.palette.cromatic.red.main
                  : theme.palette.cromatic.green.main,
            }}
            onClick={() => {
              setHasSshTunnel((value) => !value);
              setHostSSH("");
              setUserSSH("");
              setPasswordSSH("");
              setPortSSH(22);
            }}
            startIcon={<AddLink />}
          >
            {hasSshTunnel === true
              ? "Remover Túnel SSH"
              : "Adicionar Túnel SSH"}
          </Button>
        </Box>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Button
              variant="outlined"
              onClick={handleConnectDataSource}
              startIcon={
                <Sync
                  sx={{
                    animation: loading
                      ? `${spin} 1.5s linear infinite`
                      : "none",
                    fontSize: "1.8rem",
                  }}
                />
              }
              disabled={
                !host ||
                !port ||
                !datasourceType ||
                !user ||
                !password ||
                (hasSshTunnel === true &&
                  (!hostSSH || !portSSH || !userSSH || !passwordSSH)) ||
                loading
              }
            >
              Testar conexão
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Button
              sx={{
                backgroundColor: "transparent",
                color: theme.palette.cromatic.green.main,
              }}
              onClick={() => {
                setIsEditingDataSource(false);
                navigate("/configuracoes/datasource");
              }}
            >
              Cancelar
            </Button>
            <Button
              sx={{
                backgroundColor: theme.palette.text.primary,
                color: theme.palette.text.inverted,
                minHeight: 36,
              }}
              onClick={handleSubmitDataSource}
            >
              {id ? "Atualizar" : "Adicionar"}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
